import React, { useState, useEffect } from "react";
import { useAdmin } from "../../context/AdminContext";
import { Plus, Edit, Trash2, Save, X, AlertCircle, CheckCircle, Briefcase } from "lucide-react";
import ImageUploader from "./ImageUploader";

const ProjectsManager = ({ onStatsUpdate }) => {
  const { adminAPI } = useAdmin();
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [editingProject, setEditingProject] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    location: "",
    description: "",
    budget: "",
    duration: "",
    type: "Rénovation",
    status: "Terminé",
    images: []
  });

  const projectTypes = [
    "Rénovation",
    "Extension", 
    "Aménagement",
    "Rénovation énergétique",
    "Construction neuve",
    "Diagnostic",
    "Autre"
  ];

  const statusOptions = [
    "Terminé",
    "En cours",
    "Planifié",
    "Suspendu"
  ];

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await adminAPI.getProjects();
      setProjects(response.data);
      if (onStatsUpdate) onStatsUpdate(response.data.length);
    } catch (error) {
      console.error("Erreur lors du chargement des projets:", error);
      setMessage({ type: "error", text: "Erreur lors du chargement" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingProject) {
        await adminAPI.updateProject(editingProject.id, formData);
        setMessage({ type: "success", text: "Projet mis à jour avec succès !" });
      } else {
        await adminAPI.createProject(formData);
        setMessage({ type: "success", text: "Projet créé avec succès !" });
      }
      
      loadProjects();
      resetForm();
    } catch (error) {
      console.error("Erreur lors de la sauvegarde:", error);
      setMessage({ type: "error", text: "Erreur lors de la sauvegarde" });
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      ...project,
      images: project.images || []
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer ce projet ?")) {
      try {
        await adminAPI.deleteProject(id);
        setMessage({ type: "success", text: "Projet supprimé avec succès !" });
        loadProjects();
      } catch (error) {
        console.error("Erreur lors de la suppression:", error);
        setMessage({ type: "error", text: "Erreur lors de la suppression" });
      }
    }
  };

  const resetForm = () => {
    setFormData({ 
      title: "", 
      location: "", 
      description: "", 
      budget: "", 
      duration: "", 
      type: "Rénovation", 
      status: "Terminé", 
      images: [] 
    });
    setEditingProject(null);
    setShowForm(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Terminé":
        return "bg-green-100 text-green-800";
      case "En cours":
        return "bg-blue-100 text-blue-800";
      case "Planifié":
        return "bg-yellow-100 text-yellow-800";
      case "Suspendu":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Gestion des Projets</h2>
          <p className="text-gray-600 mt-1">{projects.length} projet(s) configuré(s)</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center space-x-2 bg-blue-900 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors duration-200"
        >
          <Plus className="w-4 h-4" />
          <span>Ajouter un projet</span>
        </button>
      </div>

      {message && (
        <div className={`mb-6 flex items-center space-x-2 p-4 rounded-lg ${
          message.type === "success" 
            ? "bg-green-50 text-green-800 border border-green-200" 
            : "bg-red-50 text-red-800 border border-red-200"
        }`}>
          {message.type === "success" ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">
                  {editingProject ? "Modifier le projet" : "Ajouter un projet"}
                </h3>
                <button
                  onClick={resetForm}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Titre du projet
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                      placeholder="Ex: Rénovation Maison Familiale"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Localisation
                    </label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                      placeholder="Ex: Lyon, Rhône"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                    placeholder="Description détaillée du projet..."
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Budget
                    </label>
                    <input
                      type="text"
                      name="budget"
                      value={formData.budget}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                      placeholder="Ex: 85 000€"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Durée
                    </label>
                    <input
                      type="text"
                      name="duration"
                      value={formData.duration}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                      placeholder="Ex: 4 mois"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type de projet
                    </label>
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                      required
                    >
                      <option value="">Sélectionnez un type</option>
                      {projectTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Statut
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                      required
                    >
                      {statusOptions.map(status => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Images du projet
                  </label>
                  <ImageUploader
                    images={formData.images}
                    onImagesChange={(images) => setFormData(prev => ({ ...prev, images }))}
                    maxImages={5}
                  />
                </div>

                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="flex items-center space-x-2 bg-blue-900 text-white px-6 py-2 rounded-lg hover:bg-blue-800 transition-colors duration-200"
                  >
                    <Save className="w-4 h-4" />
                    <span>Sauvegarder</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Projects List */}
      <div className="grid gap-6">
        {projects.map((project) => (
          <div key={project.id} className="bg-gray-50 rounded-lg p-6 border border-gray-200">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                {/* Project Images */}
                {project.images && project.images.length > 0 && (
                  <div className="mb-4">
                    <div className="flex space-x-2 overflow-x-auto pb-2">
                      {project.images.slice(0, 3).map((imageUrl, index) => (
                        <img
                          key={index}
                          src={imageUrl}
                          alt={`${project.title} - Image ${index + 1}`}
                          className="w-20 h-20 object-cover rounded-lg flex-shrink-0 border border-gray-200"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ))}
                      {project.images.length > 3 && (
                        <div className="w-20 h-20 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0 text-xs text-gray-600">
                          +{project.images.length - 3}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex items-center space-x-3 mb-3">
                  <Briefcase className="w-6 h-6 text-blue-900" />
                  <h3 className="text-xl font-bold text-gray-900">{project.title}</h3>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                    {project.status}
                  </span>
                </div>
                
                <p className="text-gray-600 mb-3">{project.description}</p>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-2">
                  <div>
                    <span className="font-medium text-gray-700">Lieu :</span>
                    <p className="text-gray-600">{project.location}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Budget :</span>
                    <p className="text-gray-600">{project.budget}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Durée :</span>
                    <p className="text-gray-600">{project.duration}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Type :</span>
                    <p className="text-gray-600">{project.type}</p>
                  </div>
                </div>
                <div className="text-xs text-blue-600">
                  {project.images && project.images.length > 0 
                    ? `${project.images.length} image(s)` 
                    : 'Aucune image'
                  }
                </div>
              </div>

              <div className="flex space-x-2 ml-4">
                <button
                  onClick={() => handleEdit(project)}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(project.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}

        {projects.length === 0 && (
          <div className="text-center py-12">
            <Briefcase className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun projet configuré</h3>
            <p className="text-gray-600 mb-4">Commencez par ajouter votre premier projet.</p>
            <button
              onClick={() => setShowForm(true)}
              className="bg-blue-900 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors duration-200"
            >
              Ajouter un projet
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectsManager;